import {
  ConfigProvider, Layout as AntdLayout, App, theme as antdTheme,
} from 'antd';
import locale from 'antd/locale/en_US';
import { colors } from 'configs/colors';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import utc from 'dayjs/plugin/utc';
import duration from 'dayjs/plugin/duration';
import timezone from 'dayjs/plugin/timezone';
import minMax from 'dayjs/plugin/minMax';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { createContext, useMemo, useState } from 'react';
import styles from './index.module.scss';
import Header from './components/Header';
import { RootState } from '../services/reduxStore';

dayjs.extend(advancedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);
dayjs.extend(duration);
dayjs.extend(isSameOrAfter);
dayjs.extend(minMax);

const { wrapper } = styles;
const { Content } = AntdLayout;

// eslint-disable-next-line no-underscore-dangle
const offlineModeEnabled = (window as any)._env_.REACT_APP_OFFLINE_MODE === 'true';

export type ThemeContextType = {
  theme: 'light' | 'dark';
  setTheme: (theme: 'light' | 'dark') => void;
};

export const ThemeContext = createContext<ThemeContextType>({ theme: 'light', setTheme: () => {} });

function Layout() {
  const { search } = useLocation();
  const isLoggedIn = useSelector(({ auth } : RootState) => auth.isLoggedIn);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');

  const currentAlgorithm = theme === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm;

  const themeContextValue = useMemo(() => ({ theme, setTheme }), [theme]);

  return (
    (isLoggedIn || offlineModeEnabled) ? (
      <ThemeContext.Provider value={themeContextValue}>
        <ConfigProvider
          locale={locale}
          theme={{
            algorithm: currentAlgorithm,
            token: {
              colorPrimary: colors.primary,
              colorLink: colors.primary,
              fontFamily: 'Roboto',
              ...(theme === 'dark'
                ? { colorTextBase: '#ffffff' }
                : {
                  colorTextBase: '#3a3939',
                }),
            },
            components: {
              Layout: {
                headerBg: theme === 'dark' ? '#141414' : '#ffffff',
                colorText: theme === 'dark' ? '#ffffff' : '#00000073',
              },
              Button: {
                colorPrimary: colors.primary,
                colorTextBase: '#ffffff',
                ...(theme === 'dark'
                  ? { colorTextDisabled: '#ffffff' }
                  : { colorTextDisabled: '#3a3939' }),
              },
              Segmented: {
                itemSelectedBg: colors.primary,
                ...(theme === 'dark'
                  ? {}
                  : {
                    itemSelectedColor: '#ffffff',
                  }),
              },
            },
          }}
        >
          <App>
            <AntdLayout className={wrapper}>
              {!search.includes('embedded') && <Header />}
              <Content>
                <Outlet />
              </Content>
            </AntdLayout>
          </App>
        </ConfigProvider>
      </ThemeContext.Provider>
    )
      : <Navigate to="/login" />
  );
}

export default Layout;
